import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { PRDocument, PRDetails, TableColumn } from 'src/app/shared/models/app.models';

@Component({
  selector: 'app-pr-details',
  templateUrl: './pr-details.component.html',
  styleUrls: ['./pr-details.component.scss']
})
export class PRDetailsComponent implements OnInit {
  AppConstants = AppConstants;
  prNumber: string = '';
  prDetails: PRDetails | null = null;
  evaluationCriteria: any = null;
  linkedTenderId: string | null = null;
  tableColumns: TableColumn[] = [];
  
  // Documents Dialog
  showDocumentsDialog: boolean = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Get initial route param
    this.prNumber = this.decodePRNumber(this._route.snapshot.params['id']);
    if (this.prNumber) {
      this.fetchPRDetails();
    }
    
    // Subscribe to route param changes (for navigation within same component)
    this._route.params.subscribe(params => {
      const newPrNumber = this.decodePRNumber(params['id']);
      if (newPrNumber && newPrNumber !== this.prNumber) {
        this.prNumber = newPrNumber;
        this.fetchPRDetails();
      }
    });
  }

  private decodePRNumber(value: string): string {
    if (!value) {
      return '';
    }

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  /**
   * Extracts PR data from API response
   * Handles both response.data and response.result structures
   */
  private extractPRData(response: any): any | null {
    if (!response) {
      return null;
    }

    // Check if response has data property
    if (response.data) {
      return response.data;
    }

    // Check if response has result property
    if (response.result) {
      return response.result;
    }

    return null;
  }

  /**
   * Maps raw PR data to PRDetails interface
   * Handles both camelCase and PascalCase property names
   */
  private mapToPRDetails(prData: any): PRDetails {
    return {
      prNumber: prData.prNumber || prData.PRNumber || this.prNumber,
      prName: prData.prName || prData.PRName || prData.name || prData.Name || '',
      department: prData.department || prData.Department || '',
      requisitionPurpose: prData.requisitionPurpose || prData.RequisitionPurpose || '',
      value: prData.value || prData.Value || prData.valueinQR || prData.ValueinQR || 0,
      isTC: prData.isTC ?? prData.IsTC ?? prData.icTC ?? prData.IcTC ?? false,
      approvedDate: this.parseDate(prData.approvedDate || prData.ApprovedDate),
      requestor: prData.requester || prData.Requester || '',
      requestDate: this.parseDate(prData.requestedDate || prData.RequestedDate || prData.requestDate || prData.RequestDate),
      transactionDate: prData.transactionDate || prData.TransactionDate ? this.parseDate(prData.transactionDate || prData.TransactionDate) : undefined,
      submittedBy: prData.submittedBy || prData.SubmittedBy || '',
      description: prData.description || prData.Description || '',
      justification: prData.justification || prData.Justification || '',
      specifications: prData.specifications || prData.Specifications || [],
      status: prData.status || prData.Status || '',
      linkedTender: prData.linkedTender || prData.LinkedTender,
      documents: this.mapPRDocuments(prData.documents || prData.Documents || []),
      lines: prData.lines || prData.Lines || []
    };
  }

  private mapPRDocuments(documents: any): PRDocument[] {
    if (!Array.isArray(documents)) {
      return [];
    }

    return documents
      .map((document: any): PRDocument => {
        const size = Number(document.fileSize ?? document.FileSize ?? document.size ?? document.Size ?? 0);
        const fileContent = document.fileContent || document.FileContent || '';

        return {
          name: document.name || document.Name || 'Document',
          size: Number.isFinite(size) ? size : 0,
          sizeFormatted: size > 0 ? this.formatFileSize(size) : '',
          date: document.createdAt || document.CreatedAt || '',
          attachmentId: document.attachmentId || document.AttachmentId,
          mimeType: document.mimeType || document.MimeType || document.contentType || document.ContentType,
          fileContent,
          filePath: fileContent || document.filePath || document.FilePath
        };
      })
      .filter((document: PRDocument) => !!(document.fileContent || document.filePath || document.attachmentId));
  }

  private formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, unitIndex);
    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  }

  private getDocumentUrl(document: PRDocument): string {
    if (document.fileContent || document.filePath) {
      return document.fileContent || document.filePath || '';
    }

    return document.attachmentId
      ? `${environment.API_URL}/api/Documents/DownloadDocument/${document.attachmentId}`
      : '';
  }

  /**
   * Parses date string to Date object
   * Returns current date if parsing fails
   */
  private parseDate(dateValue: any): Date {
    if (!dateValue) {
      return new Date();
    }

    if (dateValue instanceof Date) {
      return dateValue;
    }

    try {
      const parsedDate = new Date(dateValue);
      return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
    } catch {
      return new Date();
    }
  }

  async fetchPRDetails(): Promise<void> {
    // Validate PR number
    if (!this.prNumber || this.prNumber.trim() === '') {
      console.error('[PR Details] Invalid PR number provided');
      this.prDetails = null;
      return;
    }

    this._loaderService.showLoader();
    
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequisition}/${AppConstants.ACTIONS.GetApprovedRecordByPRNumber}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url, { 
          prNumber: this.prNumber
        })
      );

      // Check if request was successful
      if (!response?.isSuccess) {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch PR details';
        console.error(`[PR Details] API Error: ${errorMsg}`);
        this.prDetails = null;
        this.cdr.detectChanges();
        return;
      }

      // Extract PR data from response
      const prData = this.extractPRData(response);
      this.initializePRLinesTable();
      if (!prData) {
        console.error('[PR Details] No data found in response');
        this.prDetails = null;
        this.cdr.detectChanges();
        return;
      }

      // Map to PRDetails interface
      this.prDetails = this.mapToPRDetails(prData);
      
      // Extract evaluation criteria from PR data (if available)
      if (prData.evaluationType || prData.EvaluationType || 
          prData.technicalEvaluationCriteria || prData.TechnicalEvaluationCriteria ||
          prData.commercialEvaluationCriteria || prData.CommercialEvaluationCriteria) {
        this.evaluationCriteria = {
          evaluationType: prData.evaluationType || prData.EvaluationType,
          technicalWeightage: prData.technicalWeightage || prData.TechnicalWeightage,
          commercialWeightage: prData.commercialWeightage || prData.CommercialWeightage,
          technicalEvaluationCriteria: prData.technicalEvaluationCriteria || prData.TechnicalEvaluationCriteria || [],
          commercialEvaluationCriteria: prData.commercialEvaluationCriteria || prData.CommercialEvaluationCriteria || []
        };
      }
      
      // If there's a linked tender and no PR evaluation criteria, fetch tender evaluation criteria
      if (this.prDetails.linkedTender && !this.evaluationCriteria) {
        this.linkedTenderId = this.prDetails.linkedTender;
        await this.fetchTenderEvaluationCriteria(this.linkedTenderId);
      }
      
      this.cdr.detectChanges();
      
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[PR Details] Exception while fetching PR details for ${this.prNumber}:`, errorMessage, error);
      this.prDetails = null;
      this.cdr.detectChanges();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  getStatusColor(status: string): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('linked')) return 'green';
    if (statusLower === 'approved') return 'green';
    if (statusLower === 'pending') return 'orange';
    if (statusLower === 'rejected') return 'red';
    return 'gray';
  }


  async updateTCFlag(isTC: boolean): Promise<void> {
    if (!this.prDetails?.prNumber) {
      return;
    }

    try {
      this._loaderService.showLoader();
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequisition}/${AppConstants.ACTIONS.UpdateTCFlag}`;
      const response: any = await lastValueFrom(
        this.apiService.putByUrl(url, {
          prNumber: this.prDetails.prNumber,
          isTC
        })
      );

      if (response?.isSuccess || response?.IsSuccess) {
        this.prDetails.isTC = isTC;
        this.toasterService.showSuccess('TC flag updated successfully', 'Success');
      } else {
        const message = response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to update TC flag';
        this.toasterService.showError(message, 'Error');
      }
    } catch (error: any) {
      this.toasterService.showError(error?.message || 'Failed to update TC flag', 'Error');
    } finally {
      this._loaderService.hideLoader();
      this.cdr.detectChanges();
    }
  }
  exportPDF(): void {
    // TODO: Implement PDF export functionality
    console.log('Exporting PDF for PR:', this.prNumber);
  }

  openDocumentsDialog(): void {
    this.showDocumentsDialog = true;
  }

  closeDocumentsDialog(): void {
    this.showDocumentsDialog = false;
  }

  async viewDocument(document: PRDocument): Promise<void> {
    const documentUrl = this.getDocumentUrl(document);
    if (!documentUrl) {
      console.error('[PR Details] Document URL is unavailable:', document);
      return;
    }

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
      /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isSafari) {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to preview document: ${response.statusText}`);
      }

      const blob = await response.blob();
      const file = new File([blob], document.name, {
        type: document.mimeType || blob.type || 'application/octet-stream'
      });
      const objectUrl = URL.createObjectURL(file);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('[PR Details] Failed to preview document:', error);
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    }
  }

  async downloadDocument(document: PRDocument): Promise<void> {
    const documentUrl = this.getDocumentUrl(document);
    if (!documentUrl) {
      console.error('[PR Details] Document URL is unavailable:', document);
      return;
    }

    try {
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.statusText}`);
      }

      const blob = await response.blob();
      const file = new File([blob], document.name, {
        type: document.mimeType || blob.type || 'application/octet-stream'
      });
      const objectUrl = URL.createObjectURL(file);
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = document.name || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
    } catch (error) {
      console.error('[PR Details] Failed to download document:', error);

      const link = window.document.createElement('a');
      link.href = documentUrl;
      link.download = document.name || 'document';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  }

  goBack(): void {
    this._router.navigate([`/${AppConstants.ROUTES.APPROVED_PRS}`]);
  }

  viewTender(tenderId?: string): void {
    if (tenderId) {
      this._router.navigate([`/${AppConstants.ROUTES.TENDER}/${AppConstants.ROUTES.TENDERDETAILS}/${tenderId}`]);
    }
  }

  async fetchTenderEvaluationCriteria(tenderId: string): Promise<void> {
    if (!tenderId || tenderId.trim() === '') {
      return;
    }

    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.TENDERS}/${AppConstants.ACTIONS.GetTenderByTenderID}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url, { tenderNumber: tenderId })
      );

      if (response?.isSuccess) {
        const tenderData = response.data || response.result;
        if (tenderData) {
          this.evaluationCriteria = {
            evaluationType: tenderData.evaluationType || tenderData.EvaluationType,
            technicalWeightage: tenderData.technicalWeightage || tenderData.TechnicalWeightage,
            commercialWeightage: tenderData.commercialWeightage || tenderData.CommercialWeightage,
            technicalEvaluationCriteria: tenderData.technicalEvaluationCriteria || tenderData.TechnicalEvaluationCriteria || [],
            commercialEvaluationCriteria: tenderData.commercialEvaluationCriteria || tenderData.CommercialEvaluationCriteria || []
          };
          this.cdr.detectChanges();
        }
      }
    } catch (error: any) {
      console.error(`[PR Details] Error fetching evaluation criteria for tender ${tenderId}:`, error);
    }
  }

  
  initializePRLinesTable(): void {
  
    this.tableColumns = [
      {
        field: 'prLineNumber',
        header: 'PR Line Number',
        type: 'text'
      },
      {
        field: 'productName',
        header: 'Product Name',
        type: 'text'
      },
      {
        field: 'quantity',
        header: 'Quantity',
        type: 'text'
      }
    ];
  }

}

